/**
 * Goods Receipts — the Accounts register.
 *
 * WHAT THIS IS FOR
 *
 * Sites buy locally when something is urgent and there is no time to raise a
 * purchase order. The store-man enters an ad-hoc goods receipt and attaches
 * whatever came with the material — a delivery challan, a cash bill, sometimes
 * a proper tax invoice.
 *
 * Those are real purchases carrying real input tax credit, and Accounts had no
 * way to find them. They sat inside goods receipts, scattered across every
 * site, on a screen Accounts could not open.
 *
 * This is that list. Site-wise, with the paperwork one click away, and a record
 * of which bills have already been keyed into Tally so the same one is never
 * entered twice and none is quietly missed.
 *
 * WHY "STILL TO KEY" IS THE DEFAULT VIEW
 *
 * A register that shows everything is a register nobody finishes. The work is
 * the ad-hoc receipts that have been approved and not yet taken into Tally —
 * so that is what opens first. Everything else is a filter away.
 *
 * WHAT ACCOUNTS CANNOT DO HERE
 *
 * Approve, edit, reject or return a goods receipt. That is Stores' work and it
 * stays there. If the paperwork is missing or unreadable, Accounts raises a
 * query, which reaches the store-man who keyed it and the Head of Stores.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, CheckCircle2, ClipboardCheck, MessageSquareWarning,
  PackageSearch, Paperclip, Undo2,
} from 'lucide-react'
import {
  Alert, Card, Confirm, EmptyState, Field, Input, Loading, Modal, SearchInput,
  Select, Stat, StatusPill, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import { FileChips, NotPermitted, useAttachmentsFor } from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import { useQuery } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, rupees, localIso } from '@/lib/format'
import { CAN } from '@/lib/domain'

type Row = {
  grn_id: string
  grn_type: 'PO_BASED' | 'ADHOC'
  status: string
  site_id: string
  site_name: string | null
  received_date: string
  posting_date: string | null
  posting_period: string | null
  po_id: string | null
  vendor_id: string | null
  vendor_name: string | null
  vendor_gstin: string | null
  vendor_state: string | null
  vendor_gst_registered: boolean
  challan_no: string | null
  challan_date: string | null
  invoice_no: string | null
  invoice_date: string | null
  invoice_amount: number | null
  creator_email: string | null
  approver_email: string | null
  approval_date: string | null
  accepted_value: number
  gst_amount: number
  total_value: number
  line_count: number
  attachment_count: number
  paperwork_missing: boolean
  taken_to_tally: boolean
  tally_taken_at: string | null
  tally_taken_by: string | null
  tally_voucher_no: string | null
  adhoc_pending: boolean
}

type Summary = {
  site_id: string
  site_name: string | null
  adhoc_total: number
  still_to_key: number
  without_paperwork: number
  adhoc_value: number
  value_still_to_key: number
  oldest_unkeyed: string | null
}

const TODAY = new Date()
const START_OF_FY = new Date(
  TODAY.getMonth() >= 3 ? TODAY.getFullYear() : TODAY.getFullYear() - 1, 3, 1,
)
const iso = (d: Date) => localIso(d)

export default function GrnRegister() {
  const { profile } = useAuth()
  const toast = useToast()
  const role = profile?.role
  const mayUse = role ? CAN.viewGrnRegister.includes(role) : false
  const mayKey = role ? CAN.markGrnExtracted.includes(role) : false

  const [from, setFrom] = useState(iso(START_OF_FY))
  const [to, setTo] = useState(iso(TODAY))
  const [site, setSite] = useState('')
  const [type, setType] = useState<'ADHOC' | 'PO_BASED' | ''>('ADHOC')
  const [show, setShow] = useState<'PENDING' | 'DONE' | 'ALL'>('PENDING')
  const [search, setSearch] = useState('')
  const { sort, toggle } = useSort('received_date', 'desc')

  const [keying, setKeying] = useState<Row | null>(null)
  const [voucher, setVoucher] = useState('')
  const [keyRemarks, setKeyRemarks] = useState('')
  const [querying, setQuerying] = useState<Row | null>(null)
  const [undoing, setUndoing] = useState<Row | null>(null)
  const [busy, setBusy] = useState(false)

  // Narrowed to his own sites for a Project Manager. Same reasoning as the
  // petty cash screen: the policy is what stops him reading another site's
  // receipts; this only stops the filter offering him questions that come back
  // empty. See PettyCash.tsx for the long version.
  const { data: sites } = useQuery<{ id: string; site_name: string }>(
    'sites',
    () => {
      const q = supabase.from('sites').select('id, site_name').order('site_name')
      return role === 'project_manager' && profile?.id
        ? q.eq('project_manager_id', profile.id)
        : q
    },
    [role, profile?.id])

  const { data: rows, loading, refresh } = useQuery<Row>(
    'v_grn_register',
    () => {
      let q = supabase.from('v_grn_register').select('*')
        .gte('received_date', from).lte('received_date', to)
      if (site) q = q.eq('site_id', site)
      if (type) q = q.eq('grn_type', type)
      return q.order('received_date', { ascending: false }).limit(2000)
    },
    [from, to, site, type],
    { cacheable: false, idField: 'grn_id' },
  )

  const { data: summary } = useQuery<Summary>(
    'v_adhoc_grn_summary', () => supabase.from('v_adhoc_grn_summary').select('*'), [],
    { cacheable: false, idField: 'site_id' })

  const visible = useMemo(() => {
    const t = search.trim().toLowerCase()
    return rows.filter((r) => {
      if (show === 'PENDING' && r.taken_to_tally) return false
      if (show === 'DONE' && !r.taken_to_tally) return false
      if (!t) return true
      return [r.grn_id, r.vendor_name, r.site_name, r.challan_no, r.invoice_no,
        r.po_id, r.tally_voucher_no]
        .some((f) => (f ?? '').toLowerCase().includes(t))
    })
  }, [rows, show, search])

  // Give sortRows the column kinds. Without them a value column sorts as text
  // and ₹9,000 lands above ₹18,400 — silently, which is the worst way to be wrong.
  const sorted = useMemo(() => sortRows(visible, sort, {
    received_date: 'date', total_value: 'number',
  }), [visible, sort])
  const ids = useMemo(() => sorted.slice(0, 200).map((r) => r.grn_id), [sorted])
  const files = useAttachmentsFor('GRN', ids)

  const totals = useMemo(() => ({
    count: visible.length,
    value: visible.reduce((a, r) => a + Number(r.total_value || 0), 0),
    gst: visible.reduce((a, r) => a + Number(r.gst_amount || 0), 0),
    bare: visible.filter((r) => r.paperwork_missing).length,
  }), [visible])

  const companyPending = useMemo(() => ({
    count: summary.reduce((a, s) => a + Number(s.still_to_key || 0), 0),
    value: summary.reduce((a, s) => a + Number(s.value_still_to_key || 0), 0),
    bare: summary.reduce((a, s) => a + Number(s.without_paperwork || 0), 0),
  }), [summary])

  if (!mayUse) {
    return <NotPermitted what="the goods-receipt register"
                         who="Accounts, Stores, Procurement, the Project Head and the CMD" />
  }

  async function markKeyed() {
    if (!keying) return
    setBusy(true)
    const { error } = await supabase.rpc('rpc_mark_grn_extracted', {
      p_grn_id: keying.grn_id,
      p_voucher_no: voucher || null,
      p_remarks: keyRemarks || null,
    })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success(`${keying.grn_id} marked as taken into Tally.`)
    setKeying(null); setVoucher(''); setKeyRemarks('')
    refresh()
  }

  async function undoKeyed() {
    if (!undoing) return
    setBusy(true)
    const { error } = await supabase.rpc('rpc_unmark_grn_extracted', { p_grn_id: undoing.grn_id })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success(`${undoing.grn_id} is back on the pending list.`)
    setUndoing(null)
    refresh()
  }

  async function raiseQuery(reason: string) {
    if (!querying) return
    setBusy(true)
    const { error } = await supabase.rpc('rpc_flag_grn_query', {
      p_grn_id: querying.grn_id, p_reason: reason,
    })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success('The store-man and the Head of Stores have been told.')
    setQuerying(null)
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Goods Receipts</h1>
        <p className="text-sm text-slate-500">
          Every receipt in the company, site-wise — and the local site purchases whose
          bills still have to be keyed into Tally.
        </p>
      </div>

      {/* ---------- Company position ---------- */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Ad-hoc bills still to key" value={String(companyPending.count)}
              sub="company-wide, all dates" tone={companyPending.count > 0 ? 'warn' : 'good'}
              icon={<ClipboardCheck className="h-5 w-5" />} />
        <Stat label="Value still to key" value={rupees(companyPending.value)}
              sub="including GST" icon={<PackageSearch className="h-5 w-5" />} />
        <Stat label="Approved with no paperwork" value={String(companyPending.bare)}
              sub={companyPending.bare > 0 ? 'raise a query on these' : 'nothing outstanding'}
              tone={companyPending.bare > 0 ? 'bad' : 'good'}
              icon={<AlertTriangle className="h-5 w-5" />} />
        <Stat label="Shown below" value={String(totals.count)}
              sub={`${rupees(totals.value)} · GST ${rupees(totals.gst)}`}
              icon={<Paperclip className="h-5 w-5" />} />
      </div>

      {companyPending.bare > 0 && (
        <Alert tone="warning" title="Some approved receipts carry no purchase document">
          These were approved before the attachment rule came in. New ones cannot be
          approved without paperwork. Use <strong>Raise query</strong> on each to ask
          the site for the bill — the store-man and the Head of Stores are both told.
        </Alert>
      )}

      {/* ---------- Site-wise ---------- */}
      {summary.length > 0 && (
        <Card title="Ad-hoc purchases by site"
              subtitle="Where the local buying is happening, and where the keying is behind">
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Site</th>
                  <th className="text-right">Ad-hoc receipts</th>
                  <th className="text-right">Still to key</th>
                  <th className="text-right">Value still to key</th>
                  <th className="text-right">No paperwork</th>
                  <th>Oldest unkeyed</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((s) => (
                  <tr key={s.site_id} className={s.still_to_key > 0 ? 'bg-amber-50/40' : undefined}>
                    <td>
                      <button className="font-semibold text-oval-700 hover:underline" onClick={() => { setSite(s.site_id); setType('ADHOC') }}>
                        {s.site_name ?? s.site_id}
                      </button>
                    </td>
                    <td className="text-right">{s.adhoc_total}</td>
                    <td className="text-right font-semibold">
                      {s.still_to_key > 0
                        ? <span className="text-amber-700">{s.still_to_key}</span>
                        : <span className="text-emerald-700">0</span>}
                    </td>
                    <td className="text-right">{rupees(s.value_still_to_key)}</td>
                    <td className="text-right">
                      {s.without_paperwork > 0
                        ? <span className="text-red-700 font-semibold">{s.without_paperwork}</span>
                        : '—'}
                    </td>
                    <td className="whitespace-nowrap">
                      {s.oldest_unkeyed ? ddmmyyyy(s.oldest_unkeyed) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ---------- Filters ---------- */}
      <Card title="The register">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <Field label="Received from">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="to">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label="Site">
            <Select value={site} onChange={(e) => setSite(e.target.value)}
                    placeholder="Every site"
                    options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
          </Field>
          <Field label="Kind">
            <Select value={type} onChange={(e) => setType(e.target.value as typeof type)}
                    placeholder="Both kinds"
                    options={[
                      { value: 'ADHOC', label: 'Ad-hoc site purchase' },
                      { value: 'PO_BASED', label: 'Against a purchase order' },
                    ]} />
          </Field>
          <Field label="Show">
            <Select value={show} onChange={(e) => setShow(e.target.value as typeof show)}
                    options={[
                      { value: 'PENDING', label: 'Still to key into Tally' },
                      { value: 'DONE', label: 'Already keyed' },
                      { value: 'ALL', label: 'Everything' },
                    ]} />
          </Field>
          <Field label="Search">
            <SearchInput value={search} onChange={setSearch}
                         placeholder="Receipt, vendor, challan…" />
          </Field>
        </div>

        {loading && <Loading label="Loading goods receipts…" />}

        {!loading && sorted.length === 0 && (
          <EmptyState icon={<CheckCircle2 className="h-8 w-8" />}
                      title={show === 'PENDING' ? 'Nothing waiting' : 'Nothing to show'}
                      message={show === 'PENDING'
                        ? 'Every receipt in this period has been keyed into Tally.'
                        : 'No goods receipt matches these filters.'} />
        )}

        {!loading && sorted.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="grn_id" sort={sort} onSort={toggle}>Receipt</Th>
                  <Th id="received_date" sort={sort} onSort={toggle}>Received</Th>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="grn_type" sort={sort} onSort={toggle}>Kind</Th>
                  <Th id="vendor_name" sort={sort} onSort={toggle}>Vendor</Th>
                  <th>Bill / challan</th>
                  <Th id="total_value" sort={sort} onSort={toggle} align="right">Value</Th>
                  <th>Paperwork</th>
                  <th>Tally</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.grn_id} className={r.paperwork_missing ? 'bg-red-50/50' : 'hover:bg-oval-50'}>
                    <td className="whitespace-nowrap">
                      <Link className="font-mono text-xs font-semibold text-oval-700 hover:underline" to={`/grns/${r.grn_id}`}>{r.grn_id}</Link>
                      <div className="text-[11px] text-slate-400">
                        <StatusPill status={r.status} />
                      </div>
                    </td>
                    <td className="whitespace-nowrap">
                      {ddmmyyyy(r.received_date)}
                      {r.posting_period && (
                        <div className="text-[11px] text-slate-400">{r.posting_period}</div>
                      )}
                    </td>
                    <td>{r.site_name ?? r.site_id}</td>
                    <td>
                      {r.grn_type === 'ADHOC'
                        ? <span className="pill-amber">Ad-hoc</span>
                        : <span className="pill-grey">Against PO</span>}
                      {r.po_id && (
                        <div className="text-[11px] text-slate-400">
                          <Link className="font-semibold text-oval-700 hover:underline" to={`/pos/${r.po_id}`}>{r.po_id}</Link>
                        </div>
                      )}
                    </td>
                    <td>
                      {r.vendor_name ?? <span className="text-slate-400">—</span>}
                      {r.vendor_gstin
                        ? <div className="font-mono text-[11px] text-slate-400">{r.vendor_gstin}</div>
                        : r.vendor_gst_registered
                          ? <div className="text-[11px] text-red-600">GSTIN missing</div>
                          : <div className="text-[11px] text-slate-400">Unregistered</div>}
                    </td>
                    <td className="whitespace-nowrap text-xs">
                      {r.invoice_no && <div>Inv {r.invoice_no}</div>}
                      {r.challan_no && <div className="text-slate-500">Ch {r.challan_no}</div>}
                      {!r.invoice_no && !r.challan_no && <span className="text-slate-400">—</span>}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      <div className="font-semibold">{rupees(r.total_value)}</div>
                      {Number(r.gst_amount) > 0 && (
                        <div className="text-[11px] text-slate-400">GST {rupees(r.gst_amount)}</div>
                      )}
                    </td>
                    <td>
                      {r.attachment_count === 0
                        ? <span className="text-xs font-semibold text-red-700">None attached</span>
                        : <FileChips files={files[r.grn_id]} />}
                    </td>
                    <td className="whitespace-nowrap">
                      {r.taken_to_tally ? (
                        <div className="text-xs">
                          <span className="pill-green">Keyed</span>
                          {r.tally_voucher_no && (
                            <div className="font-mono text-[11px] text-slate-500">
                              {r.tally_voucher_no}
                            </div>
                          )}
                          <div className="text-[11px] text-slate-400">
                            {r.tally_taken_by} · {ddmmyyyy(r.tally_taken_at)}
                          </div>
                        </div>
                      ) : r.grn_type === 'ADHOC' ? (
                        <span className="pill-amber">To key</span>
                      ) : (
                        <span className="text-xs text-slate-400">via invoice</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      {mayKey && !r.taken_to_tally
                        && ['APPROVED', 'FULFILLED', 'CLOSED'].includes(r.status) && (
                        <button className="btn-secondary btn-sm"
                                onClick={() => { setKeying(r); setVoucher(''); setKeyRemarks('') }}>
                          Mark keyed
                        </button>
                      )}
                      {mayKey && r.taken_to_tally && (
                        <button className="btn-ghost btn-sm" title="Put back on the pending list"
                                onClick={() => setUndoing(r)}>
                          <Undo2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {mayKey && (
                        <button className="btn-ghost btn-sm text-amber-700" title="Raise a paperwork query"
                                onClick={() => setQuerying(r)}>
                          <MessageSquareWarning className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {sorted.length >= 200 && (
          <p className="mt-2 text-xs text-slate-500">
            Attachment links are loaded for the first 200 rows. Narrow the dates or the
            site to see the rest, or use the Excel export on the Reports screen.
          </p>
        )}
      </Card>

      <Alert tone="info" title="What Accounts can and cannot do here">
        <p>
          Open any receipt, read it, and download its paperwork. Mark an ad-hoc bill as
          keyed into Tally so it drops off the pending list. Raise a query when the
          paperwork is missing or unreadable.
        </p>
        <p className="mt-2">
          Approving, editing, returning and rejecting stay with Stores. A goods receipt
          is a record of what physically arrived at a site, and only the people who were
          there can change it.
        </p>
        <p className="mt-2">
          <Link className="font-semibold text-oval-700 hover:underline" to="/reports">Reports</Link> has the Excel export of
          this register — the working file to key from.
        </p>
      </Alert>

      {/* ---------- Mark keyed ---------- */}
      {keying && (
        <Modal open onClose={() => setKeying(null)}
               title={`Mark ${keying.grn_id} as taken into Tally`}
               subtitle={`${keying.vendor_name ?? 'Vendor'} · ${rupees(keying.total_value)}`}
               footer={<>
                 <button className="btn-secondary" onClick={() => setKeying(null)} disabled={busy}>
                   Cancel
                 </button>
                 <button className="btn-primary" onClick={() => void markKeyed()} disabled={busy}>
                   {busy ? 'Recording…' : 'Mark keyed'}
                 </button>
               </>}>
          <Alert tone="info" title="This does not post anything">
            It records that you have entered this bill in Tally, so it drops off the
            pending list and nobody keys it twice. The accounting entry itself lives in
            Tally, where it belongs.
          </Alert>

          <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
            <div><dt className="text-slate-500">Site</dt><dd>{keying.site_name}</dd></div>
            <div><dt className="text-slate-500">Received</dt><dd>{ddmmyyyy(keying.received_date)}</dd></div>
            <div><dt className="text-slate-500">Vendor GSTIN</dt>
                 <dd className="font-mono text-xs">{keying.vendor_gstin ?? '—'}</dd></div>
            <div><dt className="text-slate-500">Bill / challan</dt>
                 <dd>{keying.invoice_no ?? keying.challan_no ?? '—'}</dd></div>
            <div><dt className="text-slate-500">Taxable value</dt><dd>{rupees(keying.accepted_value)}</dd></div>
            <div><dt className="text-slate-500">GST</dt><dd>{rupees(keying.gst_amount)}</dd></div>
          </dl>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Tally voucher number" hint="Optional, but it is how you find this again">
              <Input value={voucher} onChange={(e) => setVoucher(e.target.value)}
                     placeholder="e.g. TP/1841" />
            </Field>
            <Field label="Remarks">
              <Input value={keyRemarks} onChange={(e) => setKeyRemarks(e.target.value)} />
            </Field>
          </div>
        </Modal>
      )}

      {/* ---------- Raise a query ---------- */}
      <Confirm
        open={!!querying}
        title={querying ? `Query the paperwork on ${querying.grn_id}` : ''}
        message={
          `The store-man who keyed this receipt and the Head of Stores will both be told. `
          + `Say exactly what is wrong — "query raised" on its own tells them nothing.`
        }
        confirmLabel="Send the query"
        tone="warning"
        requireReason
        reasonLabel="What is wrong with the paperwork?"
        onCancel={() => setQuerying(null)}
        onConfirm={(reason) => void raiseQuery(reason ?? '')}
      />

      {/* ---------- Undo ---------- */}
      <Confirm
        open={!!undoing}
        title={undoing ? `Put ${undoing.grn_id} back on the pending list?` : ''}
        message="Use this if it was marked keyed by mistake, or the Tally voucher was deleted."
        confirmLabel="Put it back"
        onCancel={() => setUndoing(null)}
        onConfirm={() => void undoKeyed()}
      />
    </div>
  )
}
